import type { IcsColor } from "./colors.js";

/// One element layered on top of the flag background. Coordinates are
/// in a 0-100 viewbox so the renderer can scale to any pixel size
/// without recomputation. Pennants (numerals) use the same viewbox
/// with a tapered right edge handled by the swallowtail clip.

export type FlagElement =
  | { type: "rect"; x: number; y: number; w: number; h: number; fill: IcsColor }
  | { type: "polygon"; points: string; fill: IcsColor }
  | { type: "circle"; cx: number; cy: number; r: number; fill: IcsColor }
  /// Stroke-only line, used for the saltire (X) and cross (+) flags.
  | { type: "line"; x1: number; y1: number; x2: number; y2: number; stroke: IcsColor; sw: number }
  /// Container with an SVG `transform` attribute. Used for the
  /// diagonal-stripe flag Y where rotating a few rects beats
  /// computing polygon coordinates by hand.
  | { type: "group"; transform: string; children: readonly FlagElement[] };

export interface FlagSpec {
  /// Solid background colour.
  bg: IcsColor;
  /// Layered shapes drawn over the background, in source order.
  elements: readonly FlagElement[];
  /// Swallowtail (V-cut on the right edge) — letter `A`, `B`, and
  /// some others use one. Renderer clips the right edge accordingly.
  swallowtail?: boolean;
  /// True for the digit pennants (0–9) and the Code/answering
  /// pennant. Rendered tapered: the right edge converges to a point
  /// near the middle.
  pennant?: boolean;
  /// True for the three substitute flags. Rendered as a triangle
  /// with the hoist on the left vertical edge, sloping to a single
  /// point at the fly mid-right.
  triangular?: boolean;
  /// Common name the player learns. Doubles as the spoken word in
  /// the radiotelephony alphabet (Alpha, Bravo, …).
  name: string;
  /// IMO/ITU radiotelephony pronunciation, used for digits at sea
  /// (e.g. "Unaone" for 1, "Bissotwo" for 2). Optional — letter
  /// flags omit this because their `name` is already the spoken
  /// word ("Alfa", "Bravo").
  pronunciation?: string;
  /// NATO/ICAO military spelling alphabet pronunciation for digits
  /// (Wun / Too / Tree / Fower / Fife / Six / Seven / Eight / Niner
  /// / Zero). Acceptable alongside the IMO form on VHF — most
  /// shore-side traffic uses the SMCP-mandated IMO numerals but
  /// many vessels still speak NATO out of habit.
  natoPronunciation?: string;
  /// Headline message — every flag carries a meaning on its own.
  /// Used as a hint on the EndCard.
  meaning: string;
  /// Concise label suitable for a multi-choice button. The
  /// "Identify the meaning" flashcards drill against this. Digit
  /// pennants have `null` here (their meaning IS the digit).
  shortMeaning: string | null;
  /// `letter` — A–Z alphabet flag, used for words / single-flag
  /// signals. `digit` — ICS 0–9 numeral pennant. `nato-digit` —
  /// rectangular NATO numeral (used by navies; differs from the
  /// ICS pennant design). `special` — answering pennant or
  /// substitute flags. Only `letter` and `digit` are eligible for
  /// the alphabet/digit flashcard games — special + nato-digit
  /// kinds appear in Reference only.
  kind?: "letter" | "digit" | "nato-digit" | "special";
}

export const ICS_FLAGS: Readonly<Record<string, FlagSpec>> = Object.freeze({
  // ─── Letters A–Z ───────────────────────────────────────────
  A: {
    name: "Alfa",
    meaning: "Diver down, keep well clear at slow speed.",
    shortMeaning: "Diver down",
    bg: "white",
    elements: [{ type: "rect", x: 50, y: 0, w: 50, h: 100, fill: "blue" }],
    swallowtail: true,
  },
  B: {
    name: "Bravo",
    meaning: "Dangerous cargo (loading / discharging / carrying).",
    shortMeaning: "Dangerous cargo",
    bg: "red",
    elements: [],
    swallowtail: true,
  },
  C: {
    name: "Charlie",
    meaning: "Affirmative — yes.",
    shortMeaning: "Affirmative — yes",
    bg: "blue",
    elements: [
      { type: "rect", x: 0, y: 20, w: 100, h: 20, fill: "white" },
      { type: "rect", x: 0, y: 40, w: 100, h: 20, fill: "red" },
      { type: "rect", x: 0, y: 60, w: 100, h: 20, fill: "white" },
    ],
  },
  D: {
    name: "Delta",
    meaning: "Keep clear of me — I am manoeuvring with difficulty.",
    shortMeaning: "Keep clear — manoeuvring",
    bg: "yellow",
    elements: [{ type: "rect", x: 0, y: 30, w: 100, h: 40, fill: "blue" }],
  },
  E: {
    name: "Echo",
    meaning: "Altering course to starboard.",
    shortMeaning: "Altering to starboard",
    bg: "blue",
    elements: [{ type: "rect", x: 0, y: 50, w: 100, h: 50, fill: "red" }],
  },
  F: {
    name: "Foxtrot",
    meaning: "I am disabled — communicate with me.",
    shortMeaning: "Disabled — communicate",
    bg: "white",
    elements: [
      {
        type: "polygon",
        points: "50,20 80,50 50,80 20,50",
        fill: "red",
      },
    ],
  },
  G: {
    name: "Golf",
    meaning:
      "I require a pilot. When made by fishing vessels operating in close proximity on the fishing grounds it means: 'I am hauling nets.'",
    shortMeaning: "Require a pilot",
    bg: "yellow",
    elements: [
      { type: "rect", x: 16.66, y: 0, w: 16.66, h: 100, fill: "blue" },
      { type: "rect", x: 50, y: 0, w: 16.66, h: 100, fill: "blue" },
      { type: "rect", x: 83.33, y: 0, w: 16.66, h: 100, fill: "blue" },
    ],
  },
  H: {
    name: "Hotel",
    meaning: "Pilot on board.",
    shortMeaning: "Pilot on board",
    bg: "white",
    elements: [{ type: "rect", x: 50, y: 0, w: 50, h: 100, fill: "red" }],
  },
  I: {
    name: "India",
    meaning: "Altering course to port.",
    shortMeaning: "Altering to port",
    bg: "yellow",
    elements: [{ type: "circle", cx: 50, cy: 50, r: 22, fill: "black" }],
  },
  J: {
    name: "Juliett",
    meaning:
      "I am on fire and have dangerous cargo aboard — keep well clear of me.",
    shortMeaning: "On fire, dangerous cargo — keep clear",
    bg: "blue",
    elements: [{ type: "rect", x: 0, y: 35, w: 100, h: 30, fill: "white" }],
  },
  K: {
    name: "Kilo",
    meaning: "I wish to communicate with you.",
    shortMeaning: "Wish to communicate",
    bg: "yellow",
    elements: [{ type: "rect", x: 50, y: 0, w: 50, h: 100, fill: "blue" }],
  },
  L: {
    name: "Lima",
    meaning: "You should stop your vessel immediately.",
    shortMeaning: "Stop your vessel immediately",
    bg: "yellow",
    elements: [
      { type: "rect", x: 0, y: 0, w: 50, h: 50, fill: "yellow" },
      { type: "rect", x: 50, y: 0, w: 50, h: 50, fill: "black" },
      { type: "rect", x: 0, y: 50, w: 50, h: 50, fill: "black" },
      { type: "rect", x: 50, y: 50, w: 50, h: 50, fill: "yellow" },
    ],
  },
  M: {
    name: "Mike",
    meaning: "Vessel stopped, making no way.",
    shortMeaning: "Stopped, no way",
    bg: "blue",
    elements: [
      { type: "line", x1: 0, y1: 0, x2: 100, y2: 100, stroke: "white", sw: 18 },
      { type: "line", x1: 100, y1: 0, x2: 0, y2: 100, stroke: "white", sw: 18 },
    ],
  },
  N: {
    name: "November",
    meaning: "Negative — no.",
    shortMeaning: "Negative — no",
    bg: "white",
    elements: [
      // 4x4 checker, blue on white
      ...checker(4, 4, "blue", "white"),
    ],
  },
  O: {
    name: "Oscar",
    meaning: "Man overboard.",
    shortMeaning: "Man overboard",
    bg: "red",
    elements: [{ type: "polygon", points: "0,0 100,0 0,100", fill: "yellow" }],
  },
  P: {
    name: "Papa",
    meaning:
      "In harbour: 'About to put out to sea — all persons should report on board.' At sea, made by fishing vessels: 'My nets are caught fast on an obstruction.'",
    shortMeaning: "About to put to sea",
    bg: "blue",
    elements: [{ type: "rect", x: 30, y: 30, w: 40, h: 40, fill: "white" }],
  },
  Q: {
    name: "Quebec",
    meaning:
      "My vessel is healthy and I require free pratique. (The yellow 'Quarantine' flag — flown on arrival to request clearance to enter port.)",
    shortMeaning: "Healthy — request free pratique",
    bg: "yellow",
    elements: [],
  },
  R: {
    name: "Romeo",
    meaning: "(No meaning when flown alone — used in groups.)",
    shortMeaning: null,
    bg: "red",
    elements: [
      { type: "rect", x: 40, y: 0, w: 20, h: 100, fill: "yellow" },
      { type: "rect", x: 0, y: 40, w: 100, h: 20, fill: "yellow" },
    ],
  },
  S: {
    name: "Sierra",
    meaning: "Engines going astern.",
    shortMeaning: "Engines astern",
    bg: "white",
    elements: [{ type: "rect", x: 25, y: 25, w: 50, h: 50, fill: "blue" }],
  },
  T: {
    name: "Tango",
    meaning: "Keep clear of me — pair trawling.",
    shortMeaning: "Pair trawling",
    bg: "red",
    elements: [
      { type: "rect", x: 0, y: 0, w: 33.33, h: 100, fill: "red" },
      { type: "rect", x: 33.33, y: 0, w: 33.33, h: 100, fill: "white" },
      { type: "rect", x: 66.66, y: 0, w: 33.34, h: 100, fill: "blue" },
    ],
  },
  U: {
    name: "Uniform",
    meaning: "You are running into danger.",
    shortMeaning: "Running into danger",
    bg: "white",
    elements: [
      { type: "rect", x: 0, y: 0, w: 50, h: 50, fill: "red" },
      { type: "rect", x: 50, y: 0, w: 50, h: 50, fill: "white" },
      { type: "rect", x: 0, y: 50, w: 50, h: 50, fill: "white" },
      { type: "rect", x: 50, y: 50, w: 50, h: 50, fill: "red" },
    ],
  },
  V: {
    name: "Victor",
    meaning: "Require assistance.",
    shortMeaning: "Require assistance",
    bg: "white",
    elements: [
      { type: "line", x1: 0, y1: 0, x2: 100, y2: 100, stroke: "red", sw: 16 },
      { type: "line", x1: 100, y1: 0, x2: 0, y2: 100, stroke: "red", sw: 16 },
    ],
  },
  W: {
    name: "Whiskey",
    meaning: "Require medical assistance.",
    shortMeaning: "Need medical help",
    bg: "blue",
    elements: [
      { type: "rect", x: 20, y: 20, w: 60, h: 60, fill: "white" },
      { type: "rect", x: 35, y: 35, w: 30, h: 30, fill: "red" },
    ],
  },
  X: {
    name: "X-ray",
    meaning: "Stop carrying out your intentions and watch for my signals.",
    shortMeaning: "Stop your intentions",
    bg: "white",
    elements: [
      { type: "rect", x: 40, y: 0, w: 20, h: 100, fill: "blue" },
      { type: "rect", x: 0, y: 40, w: 100, h: 20, fill: "blue" },
    ],
  },
  Y: {
    name: "Yankee",
    meaning: "Dragging anchor.",
    shortMeaning: "Dragging anchor",
    // Five diagonal stripes alternating yellow + red, running from
    // the upper hoist to the lower fly. Drawn by rotating a stack
    // of horizontal stripes around the flag centre — the rotated
    // rectangles are oversized so the corners of the viewbox are
    // covered.
    bg: "yellow",
    elements: [
      {
        type: "group",
        transform: "rotate(-45 50 50)",
        children: [
          { type: "rect", x: -50, y: -50, w: 200, h: 40, fill: "red" },
          { type: "rect", x: -50, y: 30, w: 200, h: 40, fill: "red" },
          { type: "rect", x: -50, y: 110, w: 200, h: 40, fill: "red" },
        ],
      },
    ],
  },
  Z: {
    name: "Zulu",
    meaning:
      "I require a tug. When made by fishing vessels operating in close proximity on the fishing grounds it means: 'I am shooting nets.'",
    shortMeaning: "Require a tug",
    bg: "yellow",
    elements: [
      { type: "polygon", points: "0,0 50,50 100,0", fill: "yellow" },
      { type: "polygon", points: "100,0 50,50 100,100", fill: "blue" },
      { type: "polygon", points: "100,100 50,50 0,100", fill: "red" },
      { type: "polygon", points: "0,100 50,50 0,0", fill: "black" },
    ],
  },

  // ─── Numeric pennants 0–9 ────────────────────────────────
  // Pennants are tapered — the renderer clips the right edge to a
  // point at (100, 50). Designs based on the ICS numeral pennant
  // chart.
  "0": {
    name: "Zero",
    pronunciation: "Nadazero",
    natoPronunciation: "Zero",
    meaning: "Numeral 0.",
    shortMeaning: null,
    kind: "digit",
    // ICS Vol I blazon: "Or, a pale gules" — yellow with a red
    // VERTICAL stripe down the centre.
    bg: "yellow",
    elements: [{ type: "rect", x: 33, y: 0, w: 34, h: 100, fill: "red" }],
    pennant: true,
  },
  "1": {
    name: "One",
    pronunciation: "Unaone",
    natoPronunciation: "Wun",
    meaning: "Numeral 1.",
    shortMeaning: null,
    kind: "digit",
    bg: "white",
    elements: [{ type: "circle", cx: 30, cy: 50, r: 22, fill: "red" }],
    pennant: true,
  },
  "2": {
    name: "Two",
    pronunciation: "Bissotwo",
    natoPronunciation: "Too",
    meaning: "Numeral 2.",
    shortMeaning: null,
    kind: "digit",
    // ICS Vol I blazon: "Azure, a plate" — blue with a single white
    // disc (NOT a horizontal stripe; that was the bug).
    bg: "blue",
    elements: [{ type: "circle", cx: 50, cy: 50, r: 22, fill: "white" }],
    pennant: true,
  },
  "3": {
    name: "Three",
    pronunciation: "Terrathree",
    natoPronunciation: "Tree",
    meaning: "Numeral 3.",
    shortMeaning: null,
    kind: "digit",
    bg: "white",
    elements: [
      { type: "rect", x: 0, y: 0, w: 100, h: 33, fill: "red" },
      { type: "rect", x: 0, y: 67, w: 100, h: 33, fill: "blue" },
    ],
    pennant: true,
  },
  "4": {
    name: "Four",
    pronunciation: "Kartefour",
    natoPronunciation: "Fower",
    meaning: "Numeral 4.",
    shortMeaning: null,
    kind: "digit",
    bg: "red",
    elements: [
      // White cross
      { type: "rect", x: 0, y: 40, w: 100, h: 20, fill: "white" },
      { type: "rect", x: 25, y: 0, w: 16, h: 100, fill: "white" },
    ],
    pennant: true,
  },
  "5": {
    name: "Five",
    pronunciation: "Pantafive",
    natoPronunciation: "Fife",
    meaning: "Numeral 5.",
    shortMeaning: null,
    kind: "digit",
    // ICS Vol I blazon: "Per pale or and azure" — VERTICAL split,
    // yellow at the hoist + blue at the fly.
    bg: "yellow",
    elements: [
      { type: "rect", x: 50, y: 0, w: 50, h: 100, fill: "blue" },
    ],
    pennant: true,
  },
  "6": {
    name: "Six",
    pronunciation: "Soxisix",
    natoPronunciation: "Six",
    meaning: "Numeral 6.",
    shortMeaning: null,
    kind: "digit",
    // ICS Vol I blazon: "Per fess sable and argent" — HORIZONTAL
    // split, black top half + white bottom half.
    bg: "black",
    elements: [
      { type: "rect", x: 0, y: 50, w: 100, h: 50, fill: "white" },
    ],
    pennant: true,
  },
  "7": {
    name: "Seven",
    pronunciation: "Setteseven",
    natoPronunciation: "Seven",
    meaning: "Numeral 7.",
    shortMeaning: null,
    kind: "digit",
    // ICS Vol I blazon: "Per fess or and gules" — HORIZONTAL
    // split, yellow top half + red bottom half.
    bg: "yellow",
    elements: [
      { type: "rect", x: 0, y: 50, w: 100, h: 50, fill: "red" },
    ],
    pennant: true,
  },
  "8": {
    name: "Eight",
    pronunciation: "Oktoeight",
    natoPronunciation: "Eight",
    meaning: "Numeral 8.",
    shortMeaning: null,
    kind: "digit",
    // ICS Vol I blazon: "Argent, a cross of Saint Philip gules" —
    // white with a red SALTIRE (diagonal X cross).
    bg: "white",
    elements: [
      { type: "line", x1: 0, y1: 0, x2: 100, y2: 100, stroke: "red", sw: 16 },
      { type: "line", x1: 100, y1: 0, x2: 0, y2: 100, stroke: "red", sw: 16 },
    ],
    pennant: true,
  },
  "9": {
    name: "Nine",
    pronunciation: "Novenine",
    natoPronunciation: "Niner",
    meaning: "Numeral 9.",
    shortMeaning: null,
    kind: "digit",
    // ICS Vol I blazon: "Quarterly argent, sable, gules and or" —
    // four quarters: white (chief dexter / upper-hoist), black
    // (chief sinister / upper-fly), red (base dexter /
    // lower-hoist), yellow (base sinister / lower-fly).
    bg: "white",
    elements: [
      { type: "rect", x: 0, y: 0, w: 50, h: 50, fill: "white" },
      { type: "rect", x: 50, y: 0, w: 50, h: 50, fill: "black" },
      { type: "rect", x: 0, y: 50, w: 50, h: 50, fill: "red" },
      { type: "rect", x: 50, y: 50, w: 50, h: 50, fill: "yellow" },
    ],
    pennant: true,
  },

  // ─── Special flags: answering pennant + substitutes ─────────────
  // Used as operational signals rather than letters/digits, but
  // every skipper should be able to recognise them in a hoist.
  // Designs per ICS Volume I (heraldic blazons from the
  // International Maritime Signal Flags spec).
  ANSWERING: {
    name: "Code / Answering pennant",
    meaning:
      "Acknowledges a signal — 'Code signal received and understood.' Hoisted at the dip when a signal is sighted, then close-up when the signal has been read. Also serves as the decimal point in a numerical group.",
    shortMeaning: "Code answered",
    kind: "special",
    bg: "red",
    elements: [
      // Five vertical stripes alternating red-white-red-white-red,
      // hoist (left) to fly (right). Red is the bg, white stripes
      // overlay at x=20-40 and x=60-80.
      { type: "rect", x: 20, y: 0, w: 20, h: 100, fill: "white" },
      { type: "rect", x: 60, y: 0, w: 20, h: 100, fill: "white" },
    ],
    pennant: true,
  },
  SUB1: {
    name: "First substitute",
    meaning:
      "Repeats the FIRST flag of the same class (letter / numeral) already shown in this hoist. Avoids the need to carry duplicate copies of common letters / digits.",
    shortMeaning: "Repeat 1st flag of class",
    kind: "special",
    // ICS Vol I blazon: "Or, a bordure azure which does not touch
    // the dexter end" — yellow body with a blue bordure (perimeter
    // band) that wraps the TOP, fly point, and BOTTOM edges but
    // stops short of the hoist (dexter) edge.
    bg: "yellow",
    elements: [
      // Top blue stripe — runs along the top sloping edge from
      // just inside the hoist (x=15) to the fly point (100,50).
      { type: "polygon", points: "15,0 100,50 15,15", fill: "blue" },
      // Bottom blue stripe — mirror of the top.
      { type: "polygon", points: "15,100 100,50 15,85", fill: "blue" },
    ],
    triangular: true,
  },
  SUB2: {
    name: "Second substitute",
    meaning:
      "Repeats the SECOND flag of the same class already shown in this hoist.",
    shortMeaning: "Repeat 2nd flag of class",
    kind: "special",
    bg: "blue",
    elements: [
      // Per the blazon "triangular, per pale azure and argent" —
      // vertical split, blue at the hoist + white at the fly half.
      // Drawn as a white triangle covering the fly-end of the flag.
      { type: "polygon", points: "50,25 100,50 50,75", fill: "white" },
    ],
    triangular: true,
  },
  SUB3: {
    name: "Third substitute",
    meaning:
      "Repeats the THIRD flag of the same class already shown in this hoist.",
    shortMeaning: "Repeat 3rd flag of class",
    kind: "special",
    bg: "white",
    elements: [
      // Per the blazon "argent, a fess sable" — a horizontal black
      // band ("fess") across the middle on white.
      { type: "rect", x: 0, y: 40, w: 100, h: 20, fill: "black" },
    ],
    triangular: true,
  },

  // ─── NATO numeric flags 0–9 ────────────────────────────────
  // NATO Allied Tactical Publication 1 uses RECTANGULAR numeral
  // flags (no pennant taper, no swallowtail) that look quite
  // different from the ICS pennants. Both are recognised at sea
  // and many vessels carry both sets. Heraldic blazons from
  // Wikipedia's "International maritime signal flags" article.
  N0: {
    name: "NATO 0",
    pronunciation: "Nadazero",
    natoPronunciation: "Zero",
    meaning: "NATO numeral 0.",
    shortMeaning: null,
    kind: "nato-digit",
    // "Argent, five crosses hummetty azure in saltire" — white
    // with five small blue plus-signs arranged in a saltire (X)
    // pattern: one centre + one at each diagonal corner.
    bg: "white",
    elements: [
      // Centre cross
      { type: "rect", x: 42, y: 48, w: 16, h: 4, fill: "blue" },
      { type: "rect", x: 48, y: 42, w: 4, h: 16, fill: "blue" },
      // Upper-hoist cross
      { type: "rect", x: 14, y: 20, w: 14, h: 4, fill: "blue" },
      { type: "rect", x: 19, y: 14, w: 4, h: 14, fill: "blue" },
      // Upper-fly cross
      { type: "rect", x: 72, y: 20, w: 14, h: 4, fill: "blue" },
      { type: "rect", x: 77, y: 14, w: 4, h: 14, fill: "blue" },
      // Lower-hoist cross
      { type: "rect", x: 14, y: 76, w: 14, h: 4, fill: "blue" },
      { type: "rect", x: 19, y: 71, w: 4, h: 14, fill: "blue" },
      // Lower-fly cross
      { type: "rect", x: 72, y: 76, w: 14, h: 4, fill: "blue" },
      { type: "rect", x: 77, y: 71, w: 4, h: 14, fill: "blue" },
    ],
  },
  N1: {
    name: "NATO 1",
    pronunciation: "Unaone",
    natoPronunciation: "Wun",
    meaning: "NATO numeral 1.",
    shortMeaning: null,
    kind: "nato-digit",
    // "Gules, a fess or" — red with a yellow horizontal band.
    bg: "red",
    elements: [{ type: "rect", x: 0, y: 40, w: 100, h: 20, fill: "yellow" }],
  },
  N2: {
    name: "NATO 2",
    pronunciation: "Bissotwo",
    natoPronunciation: "Too",
    meaning: "NATO numeral 2.",
    shortMeaning: null,
    kind: "nato-digit",
    // "Or, a fess gules" — yellow with a red horizontal band.
    bg: "yellow",
    elements: [{ type: "rect", x: 0, y: 40, w: 100, h: 20, fill: "red" }],
  },
  N3: {
    name: "NATO 3",
    pronunciation: "Terrathree",
    natoPronunciation: "Tree",
    meaning: "NATO numeral 3.",
    shortMeaning: null,
    kind: "nato-digit",
    // "Azure, a fess gules" — blue with a red horizontal band.
    bg: "blue",
    elements: [{ type: "rect", x: 0, y: 40, w: 100, h: 20, fill: "red" }],
  },
  N4: {
    name: "NATO 4",
    pronunciation: "Kartefour",
    natoPronunciation: "Fower",
    meaning: "NATO numeral 4.",
    shortMeaning: null,
    kind: "nato-digit",
    // "Gules, a saltire argent" — red with a white saltire (X).
    bg: "red",
    elements: [
      { type: "line", x1: 0, y1: 0, x2: 100, y2: 100, stroke: "white", sw: 16 },
      { type: "line", x1: 100, y1: 0, x2: 0, y2: 100, stroke: "white", sw: 16 },
    ],
  },
  N5: {
    name: "NATO 5",
    pronunciation: "Pantafive",
    natoPronunciation: "Fife",
    meaning: "NATO numeral 5.",
    shortMeaning: null,
    kind: "nato-digit",
    // "Or, a saltire azure" — yellow with a blue saltire (X).
    bg: "yellow",
    elements: [
      { type: "line", x1: 0, y1: 0, x2: 100, y2: 100, stroke: "blue", sw: 16 },
      { type: "line", x1: 100, y1: 0, x2: 0, y2: 100, stroke: "blue", sw: 16 },
    ],
  },
  N6: {
    name: "NATO 6",
    pronunciation: "Soxisix",
    natoPronunciation: "Six",
    meaning: "NATO numeral 6.",
    shortMeaning: null,
    kind: "nato-digit",
    // "Bendy sinister of seven argent and azure" — seven diagonal
    // stripes alternating white + blue, running top-right to
    // bottom-left (bend-sinister direction). Drawn by overlaying
    // three white stripes rotated -45° on a blue background;
    // exterior rects oversized so corners stay covered.
    bg: "blue",
    elements: [
      {
        type: "group",
        transform: "rotate(-45 50 50)",
        children: [
          { type: "rect", x: -50, y: -22, w: 200, h: 22, fill: "white" },
          { type: "rect", x: -50, y: 22, w: 200, h: 22, fill: "white" },
          { type: "rect", x: -50, y: 66, w: 200, h: 22, fill: "white" },
        ],
      },
    ],
  },
  N7: {
    name: "NATO 7",
    pronunciation: "Setteseven",
    natoPronunciation: "Seven",
    meaning: "NATO numeral 7.",
    shortMeaning: null,
    kind: "nato-digit",
    // "Gules, a pale argent" — red with a white vertical stripe.
    bg: "red",
    elements: [{ type: "rect", x: 40, y: 0, w: 20, h: 100, fill: "white" }],
  },
  N8: {
    name: "NATO 8",
    pronunciation: "Oktoeight",
    natoPronunciation: "Eight",
    meaning: "NATO numeral 8.",
    shortMeaning: null,
    kind: "nato-digit",
    // "Or, a pale azure" — yellow with a blue vertical stripe.
    bg: "yellow",
    elements: [{ type: "rect", x: 40, y: 0, w: 20, h: 100, fill: "blue" }],
  },
  N9: {
    name: "NATO 9",
    pronunciation: "Novenine",
    natoPronunciation: "Niner",
    meaning: "NATO numeral 9.",
    shortMeaning: null,
    kind: "nato-digit",
    // "Azure, a pale argent" — blue with a white vertical stripe.
    bg: "blue",
    elements: [{ type: "rect", x: 40, y: 0, w: 20, h: 100, fill: "white" }],
  },
});

/// Generate a square checker pattern as a list of `rect` elements.
function checker(
  cols: number,
  rows: number,
  on: IcsColor,
  off: IcsColor,
): FlagElement[] {
  const w = 100 / cols;
  const h = 100 / rows;
  const out: FlagElement[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const fill = (r + c) % 2 === 0 ? on : off;
      out.push({ type: "rect", x: c * w, y: r * h, w, h, fill });
    }
  }
  return out;
}

